import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { formatEur, formatDate, formatDuration, MISSION_STATUS_LABEL, MISSION_STATUS_COLOR, MISSION_TYPE_LABEL } from '@/lib/format'
import { MissionChat } from '@/components/ui/MissionChat'

interface Assignment {
  id: string
  status: string
  acceptedAt: string | null
  mission: {
    id: string
    title: string
    type: string
    status: string
    date: string
    duration: number
    amount: number
    city: string
    address: string
    description: string
    company: { id: string; name: string; logoUrl: string | null }
  }
}

function AssignmentCard({ assignment, unread = 0 }: { assignment: Assignment; unread?: number }) {
  const m = assignment.mission
  return (
    <div className="glass-card p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-white/[0.08] border border-white/[0.12] flex items-center justify-center flex-shrink-0 text-text-secondary font-semibold text-sm">
          {m.company.name[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div>
              <p className="text-text-muted text-xs mb-0.5">{m.company.name}</p>
              <h3 className="text-text-primary font-semibold text-sm">{m.title}</h3>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[#8B9AFF] font-bold">{formatEur(m.amount)}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full border mt-1 inline-block ${MISSION_STATUS_COLOR[m.status]}`}>
                {MISSION_STATUS_LABEL[m.status]}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap text-xs text-text-secondary">
            <span>📅 {formatDate(m.date)}</span>
            <span>⏱ {formatDuration(m.duration)}</span>
            <span>📍 {m.city}</span>
            <span>🏷 {MISSION_TYPE_LABEL[m.type] ?? m.type}</span>
          </div>

          {assignment.acceptedAt && (
            <p className="text-text-muted text-xs mt-2">
              Acceptée le {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(new Date(assignment.acceptedAt))}
            </p>
          )}
          {unread > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-accent/10 border border-accent/20 mt-2">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse flex-shrink-0" />
              <span className="text-accent text-xs font-medium">
                {unread} message{unread > 1 ? 's' : ''} non lu{unread > 1 ? 's' : ''} · Entreprise
              </span>
            </div>
          )}
          {['ASSIGNED', 'IN_PROGRESS', 'COMPLETED'].includes(m.status) && (
            <div className="mt-3">
              <MissionChat missionId={m.id} myRole="PROVIDER" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function MesMissionsPage() {
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [unreadMap, setUnreadMap] = useState<Record<string, number>>({})

  useEffect(() => {
    apiFetch<Assignment[]>('/assignments')
      .then(setAssignments)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))

    apiFetch<{ missionId: string; count: number }[]>('/missions/messages/unread-per-mission')
      .then((data) => {
        const m: Record<string, number> = {}
        for (const d of data) m[d.missionId] = d.count
        setUnreadMap(m)
      })
      .catch(() => {})
  }, [])

  const upcoming = assignments.filter((a) => new Date(a.mission.date) >= new Date())
  const past = assignments.filter((a) => new Date(a.mission.date) < new Date())

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Mes missions</h1>
        <p className="text-text-secondary text-sm mt-1">
          {assignments.length} mission{assignments.length !== 1 ? 's' : ''} acceptée{assignments.length !== 1 ? 's' : ''}
        </p>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">{error}</div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="glass-card h-28 animate-pulse" />)}
        </div>
      ) : assignments.length === 0 ? (
        <div className="glass-card p-14 text-center">
          <p className="text-5xl mb-4">📋</p>
          <p className="text-text-primary font-semibold mb-2">Aucune mission acceptée</p>
          <p className="text-text-secondary text-sm mb-6">Consultez les missions disponibles et acceptez-en une.</p>
          <a href="/missions-disponibles" className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block">
            Voir les missions disponibles →
          </a>
        </div>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">À venir</h2>
              <div className="space-y-3">
                {upcoming.map((a) => <AssignmentCard key={a.id} assignment={a} unread={unreadMap[a.mission.id] ?? 0} />)}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Passées</h2>
              <div className="space-y-3 opacity-70">
                {past.map((a) => <AssignmentCard key={a.id} assignment={a} unread={unreadMap[a.mission.id] ?? 0} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
